// Per-screen SEO head management for the state-machine SPA.
//
// index.html ships crawler-visible defaults (title, description, Open Graph,
// JSON-LD, noscript summary) for the splash screen. This module keeps the
// live document head in sync as the user (or a JS-rendering crawler such as
// Googlebot) moves between screens and locales:
//
// - `document.title` + meta description per screen, localized
// - robots noindex on transactional/authed screens (auth, dashboard) and on
//   every host that is not the production marketing origin, so staging and
//   preview deployments never leak into search results
// - canonical URL per indexable screen (clean path for English, ?lang=
//   variant for other locales, matching how App.tsx reflects locale in URL)
// - hreflang alternates for every supported locale plus x-default
// - og:title / og:description / og:url mirrors for social shares from
//   non-default screens
import {
  supportedLocales,
  type SupportedLocale,
  type Translate
} from "../i18n";

export type SeoScreen = "splash" | "auth" | "dashboard" | "api" | "docs";

export const PROD_ORIGIN = "https://tinkrapp.com";

// Only the production marketing host may be indexed. Staging, Cloudflare
// preview deployments, and any operator subdomains sharing this bundle are
// noindexed; their canonical still points at the production origin so any
// accidental crawl consolidates there.
const INDEXABLE_HOSTS = new Set(["tinkrapp.com", "www.tinkrapp.com"]);

const INDEXABLE_SCREENS = new Set<SeoScreen>(["splash", "api", "docs"]);

function canonicalPath(screen: SeoScreen): string {
  switch (screen) {
    case "api":
      return "/?screen=api";
    case "docs":
      return "/docs";
    default:
      return "/";
  }
}

function canonicalUrl(screen: SeoScreen, locale: SupportedLocale): string {
  const path = canonicalPath(screen);
  if (locale === "en") return `${PROD_ORIGIN}${path}`;
  const joiner = path.includes("?") ? "&" : "?";
  return `${PROD_ORIGIN}${path}${joiner}lang=${encodeURIComponent(locale)}`;
}

function upsertMetaByName(name: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[name="${name}"]`
  );
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertMetaByProperty(property: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`
  );
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(url: string | null): void {
  let link = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]'
  );
  if (url === null) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

// hreflang alternates are fully owned by this module; the data attribute
// lets us replace the set atomically on every screen/locale change.
function setHreflangAlternates(screen: SeoScreen, indexable: boolean): void {
  for (const stale of document.head.querySelectorAll(
    'link[rel="alternate"][data-seo="1"]'
  )) {
    stale.remove();
  }
  if (!indexable) return;
  const entries: Array<[string, string]> = supportedLocales.map((locale) => [
    locale,
    canonicalUrl(screen, locale)
  ]);
  entries.push(["x-default", canonicalUrl(screen, "en")]);
  for (const [hreflang, href] of entries) {
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hreflang);
    link.setAttribute("href", href);
    link.setAttribute("data-seo", "1");
    document.head.appendChild(link);
  }
}

export interface ApplySeoInput {
  screen: SeoScreen;
  locale: SupportedLocale;
  t: Translate;
  /** window.location.hostname; injectable for tests. */
  hostname: string;
}

/**
 * The provider console sets a localized per-section title ("Work queue ·
 * Tinkr", PUX-24). Parent effects run after child effects, so applySeo must
 * not overwrite it when the locale changes; it reads the registered value.
 */
let consoleDocumentTitle: string | null = null;

export function setConsoleDocumentTitle(title: string | null): void {
  consoleDocumentTitle = title;
  if (title && typeof document !== "undefined") document.title = title;
}

export function applySeo({ screen, locale, t, hostname }: ApplySeoInput): void {
  // Dashboard is never indexed. Provider sections register a localized
  // title; other consoles keep the English operator title.
  const title =
    screen !== "dashboard"
      ? t(`seo.${screen}.title`)
      : consoleDocumentTitle !== null
        ? consoleDocumentTitle
        : "Tinkr Operations";
  const description =
    screen === "dashboard"
      ? "Tinkr provider, partner, and operations dashboard"
      : t(`seo.${screen}.description`);

  const indexable =
    INDEXABLE_SCREENS.has(screen) && INDEXABLE_HOSTS.has(hostname);

  document.title = title;
  upsertMetaByName("description", description);
  upsertMetaByName(
    "robots",
    indexable ? "index, follow" : "noindex, nofollow"
  );
  setCanonical(indexable ? canonicalUrl(screen, locale) : null);
  setHreflangAlternates(screen, indexable);

  upsertMetaByProperty("og:title", title);
  upsertMetaByProperty("og:description", description);
  upsertMetaByProperty("og:url", canonicalUrl(screen, locale));
}
